import { CARD_PAGE_PX_CLASS } from '@/components/catalog/cardGrid'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { BarChart3, MessageSquare, Star, Users } from 'lucide-react'
import { Skeleton } from '@teach/ui'
import { BackButton } from '@/components/common/BackButton'
import { UnderlineTabs } from '@/components/common/UnderlineTabs'
import { useGetCourseQuery } from '@/features/editor/coursesApi'
import { ReviewsBlock } from '@/features/analytics/components/ReviewsBlock'
import { StatTile } from '@/features/analytics/components/StatTile'
import { useLanguage } from '@/hooks/useLanguage'
import { courseDisplayName } from '@/lib/localizedText'
import { formatCompactNumber, formatRating } from '@/lib/courseMeta'
import { useMessages } from '@/i18n'
import { messages } from './messages'

type Tab = 'stats' | 'reviews'

const CourseStats = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const id = Number(courseId)
  const { language: lang } = useLanguage()
  const { data: course } = useGetCourseQuery(id, { skip: !id })
  const [tab, setTab] = useState<Tab>('stats')
  const m = useMessages(messages)
  const tabs = [
    { id: 'stats' as const, label: m.tabStats, icon: BarChart3 },
    { id: 'reviews' as const, label: m.tabReviews, icon: MessageSquare },
  ]

  return (
    <div className={cn('w-full space-y-6 py-8', CARD_PAGE_PX_CLASS)}>
      <div className="flex items-center gap-2">
        <BackButton to="/dashboard" label={m.backToCourses} />
        <h1 className="min-w-0 text-3xl font-semibold">
          {course ? courseDisplayName(course, lang) : <Skeleton className="h-9 w-64" />}
        </h1>
      </div>
      <UnderlineTabs tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'reviews' ? (
        <ReviewsBlock courseId={id} />
      ) : course ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatTile icon={Star} label={m.rating} value={formatRating(course.rating.value)} />
          <StatTile
            icon={MessageSquare}
            label={m.reviews}
            value={formatCompactNumber(course.rating.reviewsCount)}
          />
          <StatTile
            icon={Users}
            label={m.completedCourse}
            value={formatCompactNumber(course.amountOfPeopleCompleted ?? 0)}
          />
        </div>
      ) : (
        <Skeleton className="h-28 w-full rounded-3xl" />
      )}
    </div>
  )
}

export default CourseStats
